'use client';

import { App, ConfigProvider } from 'antd';
import { usePathname } from 'next/navigation';
import { type ReactNode, Suspense, useMemo } from 'react';
import { Provider } from 'react-redux';
import { ToastContainer } from 'react-toastify';

import DisableMobileZoom from '~/shared/components/disable-mobile-zoom';
import GlobalErrorBoundary from '~/shared/components/global-error-boundary';
import RouterProgress from '~/shared/components/router-progress';
import StyledComponentsRegistry from '~/shared/components/styled-components-registry';
import { settings } from '~/shared/configs/config';
import { RootContext } from '~/shared/configs/root-context';
import { checkIsAdminPanel, checkIsPlatform } from '~/shared/helpers/check-helpers';
import { ColorSchemeProvider } from '~/shared/hooks/use-color-scheme';
import { ApolloWrapper } from '~/shared/libs/apollo/apollo-provider';
import store from '~/shared/store';
import type { UserType } from '~/shared/types/user-type';
import type { Locale } from '~/universal/constants';

type ClientProvidersProps = {
  lng: Locale;
  children: ReactNode;
  user: UserType;
  isStorage?: boolean;
};

const Providers = ({ lng, children }) => {
  return (
    <ColorSchemeProvider>
      <GlobalErrorBoundary lng={lng}>
        <StyledComponentsRegistry>
          <ConfigProvider theme={settings.theme}>
            <App>
              <ApolloWrapper>
                <Suspense fallback={null}>
                  <RouterProgress delay={80} />
                </Suspense>
                <ToastContainer />
                {children}
              </ApolloWrapper>
            </App>
          </ConfigProvider>
        </StyledComponentsRegistry>
      </GlobalErrorBoundary>
    </ColorSchemeProvider>
  );
};

const WebClientProviders = ({ lng, user, isStorage, children }: ClientProvidersProps) => {
  const pathname = usePathname();

  const isAdminPanel = checkIsAdminPanel(pathname);
  const isPlatform = checkIsPlatform(pathname);
  const rootContext = useMemo(
    () => ({
      lng,
      user,
      showHeader: true,
      isPlatform,
      isAdminPanel,
    }),
    [lng, user, isPlatform, isAdminPanel],
  );

  return isStorage ? (
    <Provider store={store}>
      <RootContext.Provider value={rootContext}>
        <Providers isStorage lng={lng}>
          <DisableMobileZoom />
          {children}
        </Providers>
      </RootContext.Provider>
    </Provider>
  ) : (
    <Providers lng={lng}>{children}</Providers>
  );
};

export default WebClientProviders;
